// frontend-react/src/pages/SetupPasswordPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Result,
  Typography,
  Space,
  Alert,
  Progress,
  Tag,
} from 'antd';
import { LockOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';

const { Title, Text } = Typography;

interface TokenVerification {
  valid: boolean;
  user_name?: string;
  user_email?: string;
}

interface PasswordPolicy {
  min_length: number; // e.g. 14
  required_classes: number; // e.g. 3 (out of 4)
  allowed_special_chars: string; // e.g. "!@#$.../?"
  rotation_days: number; // e.g. 90
}

type PasswordCheckResult = {
  lenOK: boolean;
  classesOK: boolean;
  classesCount: number;
  hasUpper: boolean;
  hasLower: boolean;
  hasDigit: boolean;
  hasSpecial: boolean;
};

const DEFAULT_PASSWORD_POLICY: PasswordPolicy = {
  min_length: 14,
  required_classes: 3,
  // ⚠️ 注意：字串裡的 \ 要用 \\ 表示
  allowed_special_chars: '!@#$%^&*()_-+=[]{};\':"\\|,.<>/?',
  rotation_days: 90,
};

// 產生「只允許常見英文半形特殊符號」的 regex（跟後端 allowed_special_chars 同步）
function buildAllowedSpecialRegex(allowedChars: string): RegExp {
  // 在 regex 的字元集合 [] 中，需要特別跳脫： \ - ] ^
  const escaped = allowedChars.replace(/[-\\\]^]/g, '\\$&');
  return new RegExp(`[${escaped}]`);
}

// Step B：純前端即時檢核（不打 API）
function checkPasswordLocal(
  pwd: string,
  policy: PasswordPolicy,
  specialRegex: RegExp
): PasswordCheckResult {
  const hasUpper = /[A-Z]/.test(pwd);
  const hasLower = /[a-z]/.test(pwd);
  const hasDigit = /[0-9]/.test(pwd);
  const hasSpecial = specialRegex.test(pwd);

  const classesCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length;

  return {
    lenOK: pwd.length >= policy.min_length,
    classesOK: classesCount >= policy.required_classes,
    classesCount,
    hasUpper,
    hasLower,
    hasDigit,
    hasSpecial,
  };
}

function getMissingClasses(r: PasswordCheckResult) {
  const missing: string[] = [];
  if (!r.hasUpper) missing.push('大寫');
  if (!r.hasLower) missing.push('小寫');
  if (!r.hasDigit) missing.push('數字');
  if (!r.hasSpecial) missing.push('符號');
  return missing;
}

const SetupPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState<TokenVerification | null>(null);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendForm] = Form.useForm();

  // ✅ 方案二：policy 從後端抓（抓不到就用預設）
  const [policy, setPolicy] = useState<PasswordPolicy>(DEFAULT_PASSWORD_POLICY);

  // Step B：即時檢核用 state
  const [passwordLive, setPasswordLive] = useState('');
  const [confirmLive, setConfirmLive] = useState('');

  const token = searchParams.get('token');

  const specialRegex = useMemo(
    () => buildAllowedSpecialRegex(policy.allowed_special_chars),
    [policy.allowed_special_chars]
  );

  useEffect(() => {
    // 方案二：進來頁面就抓 policy（不依賴 token）
    const fetchPolicy = async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/auth/password-policy`);
        if (!res.ok) return; // 保持 DEFAULT
        const data = (await res.json()) as PasswordPolicy;

        // 最低限度防呆：避免後端漏欄位導致前端炸掉
        if (
          typeof data?.min_length === 'number' &&
          typeof data?.required_classes === 'number' &&
          typeof data?.allowed_special_chars === 'string' &&
          typeof data?.rotation_days === 'number'
        ) {
          setPolicy(data);
        }
      } catch {
        // ignore：保持 DEFAULT
      }
    };

    fetchPolicy();
  }, []);

  useEffect(() => {
    if (!token) {
      message.error('缺少驗證 Token');
      setVerifying(false);
      return;
    }

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/verify-reset-token?token=${token}`);

      if (!response.ok) {
        throw new Error('Token 已過期或無效');
      }

      const data = await response.json();
      setUserInfo(data);
      setTokenValid(data.valid);
    } catch (error: any) {
      message.error(error.message || 'Token 驗證失敗');
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (values: { password: string; confirmPassword: string }) => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: values.password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '設定密碼失敗');
      }

      setSuccess(true);
      message.success('密碼設定成功！即將跳轉到登入頁面...');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      message.error(error.message || '設定密碼失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async (values: { email: string }) => {
    setResending(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/resend-setup-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '重新發送失敗');
      }

      message.success('設定密碼郵件已重新發送，請檢查您的信箱');
      resendForm.resetFields();
    } catch (error: any) {
      message.error(error.message || '重新發送失敗');
    } finally {
      setResending(false);
    }
  };

  if (verifying) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--color-bg)',
        }}
      >
        <Card style={{ width: 400, textAlign: 'center' }}>
          <LoadingOutlined style={{ fontSize: 48, color: '#1E88A8' }} />
          <Title level={4} style={{ marginTop: 16 }}>
            驗證中...
          </Title>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--color-bg)',
        }}
      >
        <Card style={{ width: 500 }}>
          <Result
            status='error'
            title='連結已過期或無效'
            subTitle='此設定密碼連結已過期或已被使用'
          />

          <div style={{ padding: '0 24px 24px' }}>
            <Alert
              message='需要重新發送設定密碼郵件？'
              description='請輸入您的 Email，我們會重新發送設定密碼的連結給您'
              type='info'
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form form={resendForm} layout='vertical' onFinish={handleResendEmail}>
              <Form.Item
                name='email'
                label='Email'
                rules={[
                  { required: true, message: '請輸入 Email' },
                  { type: 'email', message: '請輸入有效的 Email' },
                ]}
              >
                <Input placeholder='請輸入您的 Email' size='large' />
              </Form.Item>

              <Form.Item>
                <Space style={{ width: '100%' }}>
                  <Button type='primary' htmlType='submit' loading={resending} size='large'>
                    重新發送郵件
                  </Button>
                  <Button size='large' onClick={() => navigate('/login')}>
                    返回登入
                  </Button>
                </Space>
              </Form.Item>
            </Form>

            <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
              <Text type='secondary' style={{ fontSize: 12 }}>
                💡 提示：5 分鐘內只能重新發送一次郵件
              </Text>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'var(--color-bg)',
        }}
      >
        <Card style={{ width: 500 }}>
          <Result
            status='success'
            title='密碼設定成功！'
            subTitle='您的帳號已啟用，即將跳轉到登入頁面'
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
          />
        </Card>
      </div>
    );
  }

  const r = checkPasswordLocal(passwordLive, policy, specialRegex);
  const missing = getMissingClasses(r);
  const basicPass = r.lenOK && r.classesOK;
  const confirmMismatch = Boolean(confirmLive) && confirmLive !== passwordLive;
  const canSubmitHint = basicPass && !confirmMismatch;

  // 進度條：把長度當 1 分，類型達標當 1 分（總分 2）
  const score = (r.lenOK ? 1 : 0) + (r.classesOK ? 1 : 0);
  const percent = Math.round((score / 2) * 100);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'var(--color-bg)',
      }}
    >
      <Card style={{ width: 520 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <LockOutlined style={{ fontSize: 48, color: '#1E88A8' }} />
          <Title level={2} style={{ marginTop: 16, marginBottom: 8 }}>
            設定密碼
          </Title>
          {userInfo && (
            <Space direction='vertical' size={4}>
              <Text type='secondary'>歡迎，{userInfo.user_name}</Text>
              <Text type='secondary' style={{ fontSize: 12 }}>
                {userInfo.user_email}
              </Text>
            </Space>
          )}
        </div>

        {/* 規則提醒（由 policy 生成） */}
        <Alert
          message='密碼要求'
          description={
            <div>
              <div>• 密碼長度：至少 {policy.min_length} 字元</div>
              <div>• 至少包含 {policy.required_classes} 項：大寫 / 小寫 / 數字 / 符號</div>
              <div>• 每 {policy.rotation_days} 天需更換一次密碼</div>
              <div style={{ color: '#666' }}>※ 限制：密碼不可等於或包含於帳號內</div>
            </div>
          }
          type='info'
          showIcon
          style={{ marginBottom: 12 }}
        />

        {/* 即時檢核（不需要查看詳細，直接顯示） */}
        {passwordLive ? (
          <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 10 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <div style={{ fontWeight: 600 }}>
                密碼檢核：{canSubmitHint ? '✅ 通過' : '⚠ 尚未通過'}
                {!canSubmitHint && missing.length > 0 ? `（缺少：${missing.join('、')}）` : ''}
              </div>
              <div style={{ minWidth: 160 }}>
                <Progress percent={percent} size='small' />
              </div>
            </div>

            <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Tag color={r.lenOK ? 'green' : 'red'}>
                長度 ≥ {policy.min_length} {r.lenOK ? '✓' : '✕'}
              </Tag>
              <Tag color={r.classesOK ? 'green' : 'red'}>
                類型 {policy.required_classes}/4（{r.classesCount}/4）{r.classesOK ? '✓' : '✕'}
              </Tag>
              <Tag color={r.hasUpper ? 'green' : 'default'}>大寫 {r.hasUpper ? '✓' : '✕'}</Tag>
              <Tag color={r.hasLower ? 'green' : 'default'}>小寫 {r.hasLower ? '✓' : '✕'}</Tag>
              <Tag color={r.hasDigit ? 'green' : 'default'}>數字 {r.hasDigit ? '✓' : '✕'}</Tag>
              <Tag color={r.hasSpecial ? 'green' : 'default'}>符號 {r.hasSpecial ? '✓' : '✕'}</Tag>
            </div>

            {confirmMismatch && (
              <div style={{ marginTop: 10, color: '#cf1322' }}>❌ 兩次輸入的密碼不一致</div>
            )}
          </div>
        ) : null}

        <Form
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          onValuesChange={(_, allValues) => {
            setPasswordLive(allValues.password || '');
            setConfirmLive(allValues.confirmPassword || '');
          }}
        >
          <Form.Item
            name='password'
            label='新密碼'
            rules={[
              { required: true, message: '請輸入密碼' },
              () => ({
                validator(_, value) {
                  const v = String(value || '');
                  if (!v) return Promise.resolve();

                  const rr = checkPasswordLocal(v, policy, specialRegex);
                  if (!rr.lenOK)
                    return Promise.reject(new Error(`密碼至少需 ${policy.min_length} 字元`));
                  if (!rr.classesOK)
                    return Promise.reject(
                      new Error(
                        `需包含至少 ${policy.required_classes} 種字元類型（大寫/小寫/數字/英文符號）`
                      )
                    );
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={`請輸入至少 ${policy.min_length} 個字元`}
              size='large'
            />
          </Form.Item>

          <Form.Item
            name='confirmPassword'
            label='確認密碼'
            dependencies={['password']}
            rules={[
              { required: true, message: '請確認密碼' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('兩次輸入的密碼不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder='再次輸入密碼' size='large' />
          </Form.Item>

          <Form.Item style={{ marginTop: 24 }}>
            <Button
              type='primary'
              htmlType='submit'
              size='large'
              block
              loading={loading}
              disabled={!canSubmitHint}
            >
              設定密碼並啟用
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Text type='secondary' style={{ fontSize: 12 }}>
            設定完成後，請使用您的 Email 和密碼登入系統
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default SetupPasswordPage;
